import React from 'react';
import { TeamMember } from '../../types'; // Adjust path if necessary
import { Button, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@pagespeedify/ui"; // Removed Card, CardContent, CardHeader, CardTitle
import { Loader2 } from 'lucide-react';

interface MemberListProps {
  members: TeamMember[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRemove: (memberId: string) => void;
  isRemovingMemberId: string | null;
  refetchMembers: () => void;
}

export const MemberList: React.FC<MemberListProps> = ({
  members,
  isLoading,
  isError,
  error,
  onRemove,
  isRemovingMemberId,
  refetchMembers,
}) => {
  const handleRemoveClick = (memberId: string) => {
    onRemove(memberId);
  };

  if (isLoading) {
    // Return Skeleton directly without Card wrappers
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
      </div>
    );
  }

  if (isError) {
    // Return error message directly
    return (
      <div>
        <p className="text-red-600">Error loading members: {error?.message || 'Unknown error'}</p>
        <Button onClick={refetchMembers} className="mt-2" size="sm" variant="outline">Retry</Button>
      </div>
    );
  }

  if (!members || members.length === 0) {
    // Return empty message directly
    return <p>No members have been added to this team yet.</p>;
  }

  // Return Table directly
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Joined</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => (
          <TableRow key={member.id}>
            <TableCell>{member.email}</TableCell>
            <TableCell>{member.first_name || member.last_name ? `${member.first_name || ''} ${member.last_name || ''}`.trim() : '-'}</TableCell>
            <TableCell>{new Date(member.joined_at).toLocaleDateString()}</TableCell>
            <TableCell className="text-right">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleRemoveClick(member.id)}
                disabled={!!isRemovingMemberId || isLoading}
              >
                {isRemovingMemberId === member.id ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove'
                )}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};